import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Scale } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { SITE } from "@/lib/site";
import { useSEO, SITE_ORIGIN } from "@/lib/seo";

const PRIMARY_BTN =
  "inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.25)]";
const GHOST_LIGHT =
  "inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 text-[#A8871B] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300";
const GHOST_DARK =
  "inline-flex items-center gap-2 rounded-full border border-[#C9A227]/50 text-[#E8CC6E] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300";

const GuideLink = ({ to, className, testid, children, external }) =>
  external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>
      {children}
    </a>
  ) : (
    <Link to={to} data-testid={testid} className={className}>
      {children}
    </Link>
  );

/**
 * Shared educational guide layout (the "Smart Guide" SEO formula):
 * hero → key facts → comparison table → content cards → honest take →
 * FAQ (with FAQPage schema) → CTA band. Config-driven; see the guide pages.
 */
export default function GuideTemplate({ cfg }) {
  const [openFaq, setOpenFaq] = useState(0);
  const { seo, hero, facts, showcase, table, cards, honestTake, faqs, cta, moreGuides } = cfg;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: seo.title,
    description: seo.description,
    canonicalPath: seo.path,
    ogType: "article",
    ogImage: `${SITE_ORIGIN}${seo.image}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  return (
    <main data-testid={`${seo.testid}-page`}>
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-[164px] pb-20 lg:pt-[196px] lg:pb-24">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>{hero.eyebrow}</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-neutral-900 leading-[1.08]" data-testid={`${seo.testid}-heading`}>
              {hero.title} <span className="text-[#C9A227]">{hero.titleAccent}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-xl">{hero.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              {hero.ctas.map((c, i) => (
                <GuideLink
                  key={c.to}
                  to={c.to}
                  external={c.external}
                  testid={`${seo.testid}-cta-${i}`}
                  className={i === 0 ? PRIMARY_BTN : GHOST_LIGHT}
                >
                  {c.label} {i === 0 && <ArrowRight size={15} strokeWidth={1.5} />}
                </GuideLink>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle,rgba(232,204,110,0.25),transparent_65%)]" />
              <img
                src={hero.image}
                alt={hero.imageAlt}
                data-testid={`${seo.testid}-hero-image`}
                className="relative w-full h-[300px] lg:h-[420px] object-cover rounded-sm border border-[#E8DCC4] shadow-[0_30px_60px_rgba(0,0,0,0.12)]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Key facts */}
      {facts && (
        <section className="py-16 lg:py-20" data-testid={`${seo.testid}-facts`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900">
                {facts.title} <span className="text-[#C9A227]">{facts.titleAccent}</span>
              </h2>
              {facts.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 text-base leading-relaxed text-neutral-600 max-w-2xl">{p}</p>
              ))}
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="grid grid-cols-3 gap-4">
                {facts.items.map(({ icon: Icon, label }, i) => (
                  <div key={i} className="bg-white border border-[#E8DCC4] rounded-sm p-5 text-center" data-testid={`${seo.testid}-fact-${i}`}>
                    <Icon size={22} strokeWidth={1.25} className="text-[#C9A227] mx-auto" />
                    <p className="text-xs text-neutral-700 mt-3 leading-snug font-medium">{label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Showcase: feature image + explainer */}
      {showcase && (
        <section className={`py-16 lg:py-20 ${showcase.dark ? "bg-white border-y border-[#E8DCC4]" : ""}`} data-testid={`${seo.testid}-showcase`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className={`lg:col-span-6 ${showcase.imageFirst ? "lg:order-2" : ""}`}>
              <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900">
                {showcase.title} <span className="text-[#C9A227]">{showcase.titleAccent}</span>
              </h2>
              {showcase.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 text-base leading-relaxed text-neutral-600 max-w-xl">{p}</p>
              ))}
            </Reveal>
            <Reveal delay={0.12} className={`lg:col-span-6 ${showcase.imageFirst ? "lg:order-1" : ""}`}>
              <div className="overflow-hidden rounded-sm border border-[#E8DCC4] shadow-[0_30px_60px_rgba(0,0,0,0.10)]">
                <img
                  src={showcase.image}
                  alt={showcase.imageAlt}
                  className="w-full h-[280px] lg:h-[380px] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Comparison table */}
      {table && (        <section className="py-16 lg:py-20 bg-white border-y border-[#E8DCC4]" data-testid={`${seo.testid}-compare`}>
          <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900 text-center">
                {table.title} <span className="text-[#C9A227]">{table.titleAccent}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 overflow-x-auto">
                <table className="w-full text-sm" data-testid={`${seo.testid}-table`}>
                  <thead>
                    <tr className="border-b-2 border-[#C9A227]/40">
                      <th className="text-left py-3 pr-4 text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold"></th>
                      {table.columns.map((col) => (
                        <th key={col} className="text-left py-3 pr-4 last:pr-0 text-xs uppercase tracking-[0.15em] text-[#A8871B] font-bold">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row, i) => (
                      <tr key={i} className="border-b border-[#E8DCC4]" data-testid={`${seo.testid}-row-${i}`}>
                        <td className="py-3.5 pr-4 font-semibold text-neutral-800 whitespace-nowrap">{row.label}</td>
                        {row.values.map((v, j) => (
                          <td key={j} className="py-3.5 pr-4 last:pr-0 text-neutral-600">{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Content cards */}
      {cards && (
        <section className="py-16 lg:py-20" data-testid={`${seo.testid}-cards`}>
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900">
                {cards.title} <span className="text-[#C9A227]">{cards.titleAccent}</span>
              </h2>
            </Reveal>
            <div className={`mt-10 grid gap-6 max-w-5xl ${cards.items.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {cards.items.map(({ icon: Icon, title, text }, i) => (
                <Reveal key={title} delay={0.06 * i}>
                  <div className="bg-white border border-[#E8DCC4] rounded-sm p-7 h-full" data-testid={`${seo.testid}-card-${i}`}>
                    <Icon size={24} strokeWidth={1.25} className="text-[#C9A227]" />
                    <h3 className="font-display text-lg text-neutral-900 mt-4">{title}</h3>
                    <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* Honest take */}
            {honestTake && (
              <Reveal delay={0.1}>
                <div className="mt-14 bg-[#F5F3EC] border border-[#E8DCC4] rounded-sm overflow-hidden max-w-4xl grid md:grid-cols-5" data-testid={`${seo.testid}-honest-take`}>
                  {honestTake.image && (
                    <div className="md:col-span-2 overflow-hidden">
                      <img src={honestTake.image} alt={honestTake.imageAlt} className="w-full h-64 md:h-full object-cover" />
                    </div>
                  )}
                  <div className={`${honestTake.image ? "md:col-span-3" : "md:col-span-5"} p-8 lg:p-10`}>
                    <div className="flex items-start gap-5">
                      <Scale size={26} strokeWidth={1.25} className="text-[#C9A227] mt-1 shrink-0" />
                      <div>
                        <h3 className="font-display text-xl text-neutral-900">{honestTake.title}</h3>
                        <p className="text-sm md:text-base text-neutral-600 mt-3 leading-relaxed">{honestTake.text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#E8DCC4]" data-testid={`${seo.testid}-faq`}>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900 text-center">
              Questions we hear <span className="text-[#C9A227]">every week</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={0.04 * i}>
                <div className="border border-[#E8DCC4] rounded-sm bg-[#FDFBF7]" data-testid={`${seo.testid}-faq-${i}`}>
                  <button
                    data-testid={`${seo.testid}-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-4"
                  >
                    <span className="text-sm md:text-base font-semibold text-neutral-800">{f.q}</span>
                    <ChevronDown
                      size={16}
                      strokeWidth={1.5}
                      className={`text-[#C9A227] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openFaq === i && (
                    <p className="px-6 pb-5 text-sm text-neutral-600 leading-relaxed" data-testid={`${seo.testid}-faq-answer-${i}`}>
                      {f.a}
                    </p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20" data-testid={`${seo.testid}-cta`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="relative overflow-hidden bg-[#0D0D0D] rounded-sm p-8 lg:p-12">
              {cta.bgImage && (
                <div className="absolute inset-0 opacity-30">
                  <img src={cta.bgImage} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="relative max-w-xl">
                <h2 className="font-display text-2xl md:text-3xl font-light text-white mt-2" data-testid={`${seo.testid}-cta-heading`}>
                  {cta.heading}
                </h2>
                <p className="text-sm md:text-base text-neutral-400 mt-3 leading-relaxed">{cta.text}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  {cta.buttons.map((b, i) => (
                    <GuideLink
                      key={b.to}
                      to={b.to}
                      external={b.external}
                      testid={`${seo.testid}-cta-btn-${i}`}
                      className={i === 0 ? PRIMARY_BTN : GHOST_DARK}
                    >
                      {b.label} {i === 0 && <ArrowRight size={15} strokeWidth={1.5} />}
                    </GuideLink>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {moreGuides && (
            <Reveal delay={0.1}>
              <div className="mt-12" data-testid={`${seo.testid}-more-guides`}>
                <p className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold mb-4">More from the jeweler's bench</p>
                <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
                  {moreGuides.map((g) => (
                    <Link
                      key={g.to}
                      to={g.to}
                      data-testid={`${seo.testid}-more-${g.to.replace(/\//g, "")}`}
                      className="group flex items-center justify-between bg-white border border-[#E8DCC4] rounded-sm px-6 py-5 hover:border-[#C9A227] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] transition-[border-color,transform,box-shadow] duration-300"
                    >
                      <span className="font-display text-base text-neutral-900">{g.label}</span>
                      <ArrowRight size={16} strokeWidth={1.5} className="text-[#C9A227] group-hover:translate-x-1 transition-transform duration-300 shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
